/** The tour detail as draft baselines (plan §3 `projectTourBaseline`): the read shape
 *  mapped onto the records the draft edits, plus what compile needs to send whole
 *  objects and whole collections.
 *
 *  - `tour:<id>` — the catalog's paths (`name`, `context.notes`, `meeting_point.address`,
 *    `launch_policy` as an id, `atag` as sorted ids, file paths as `{id}` or null), every
 *    known context key (absent = null), the tour's own pre-check-in keys
 *    (`pre_check_settings.<k>`), and the membership lists of its collections
 *    (`additional_custom_ids.order`, `tag_categories.order`, `pre_trip_items.order`,
 *    `itinerary.days`).
 *  - one record per collection row (`alias:12`, `tagcat:3`, `pretrip:9`);
 *  - the itinerary (P4, `itinerary/model.ts`): one record per day, stop and activity row
 *    (`day:12`, `slot:40`, `activity:401`) with their membership lists (`day.slots`,
 *    `slot.activities`), and the tour-level route file (`itinerary.kml`).
 *
 *  Pure; `useTourBaseline` memoises it on the tour's `updated_ts` (every write bumps it). */
import { useMemo } from 'react';

import type { TourDetail } from '@/api/tours';
import { recordKey, type RecordKey } from '@/features/records/draft/changeSet';

import {
  CONTEXT_KEYS,
  contextBaseline,
  contextPath,
  PRECHECK,
  PRECHECK_DEFAULTS,
  TOUR_ENTITY,
  WAIVER_REQUIRED_PATH,
  waiverRequiredOf,
  type WholeObjectKey,
} from './catalog';
import { projectItinerary, TOUR_KML, type ItineraryRaw } from '../itinerary/model';

import { COLLECTIONS, ITINERARY_DAYS, type CollectionKey } from './collections';

type Raw = Record<string, unknown> & { id: number };

export interface TourBaseline {
  tourId: number;
  /** `tour:<id>`. */
  rec: RecordKey;
  /** The `expected_updated_ts` a PATCH from this baseline sends. */
  updatedTs: string | null;
  /** Path-space baselines (DraftProvider `baselines`). */
  paths: Record<RecordKey, Record<string, unknown>>;
  /** The stored objects compile overlays (rule 3). `photo` is never part of the meeting
   *  point here: sending the stored photo back would delete it server-side. */
  stored: Record<WholeObjectKey, Record<string, unknown> | null>;
  /** The server's raw rows by id, per collection (rule 4). */
  raw: Record<CollectionKey, Map<number, Raw>>;
  /** The itinerary's stored days, stops and activity rows by id (rule 5). */
  itinerary: ItineraryRaw;
}

const sortedIds = (xs: ReadonlyArray<{ id: number }> | null | undefined) =>
  (xs ?? []).map((x) => x.id).sort((a, b) => a - b);

export function projectTourBaseline(detail: TourDetail): TourBaseline {
  const rec = recordKey(TOUR_ENTITY, detail.id);
  const mp = detail.meeting_point;
  const ctx = detail.context ?? {};
  const own = detail.pre_check_settings_own ?? {};

  const tour: Record<string, unknown> = {
    custom_id: detail.custom_id ?? null,
    name: detail.name,
    internal_name: detail.internal_name ?? null,
    description: detail.description ?? null,
    start_timezone: detail.start_timezone ?? null,
    use_as_template: detail.use_as_template,
    cover_image: detail.cover_image ? { id: detail.cover_image.id } : null,
    'meeting_point.name': mp?.name ?? null,
    'meeting_point.address': mp?.address ?? null,
    'meeting_point.latitude': mp?.latitude ?? null,
    'meeting_point.longitude': mp?.longitude ?? null,
    'meeting_point.maps_url': mp?.maps_url ?? null,
    'meeting_point.comment': mp?.comment ?? null,
    'meeting_point.photo': mp?.photo ? { id: mp.photo.id } : null,
    location_sharing: detail.location_sharing,
    mytrips_display: detail.mytrips_display,
    has_group_chat: detail.has_group_chat,
    exclude_from_chat: detail.exclude_from_chat,
    disable_internal_chat: detail.disable_internal_chat,
    show_all_users_to_guides_in_nonbespoke_tours: detail.show_all_users_to_guides_in_nonbespoke_tours ?? null,
    use_operator_defaults: detail.use_operator_defaults,
    chat_priority: detail.chat_priority,
    effective_display_currency: detail.effective_display_currency,
    start_date: detail.start_date ?? null,
    end_date: detail.end_date ?? null,
    departures: (detail.departures ?? [])
      .map((d) => d.tag?.id)
      .filter((id): id is number => typeof id === 'number')
      .sort((a, b) => a - b),
    length: detail.length ?? null,
    length_is_manual: detail.length_is_manual,
    launch_policy: detail.launch_policy?.id ?? null,
    atag: sortedIds(detail.atag),
    tags: sortedIds(detail.tags),
    crosssells: sortedIds(detail.crosssells),
    'review.name': detail.review?.name ?? null,
    'review.url': detail.review?.url ?? null,
    'photo_sharing.name': detail.photo_sharing?.name ?? null,
    'photo_sharing.url': detail.photo_sharing?.url ?? null,
    positive_review_target: detail.positive_review_target,
    positive_review_url: detail.positive_review_url ?? null,
    allow_selfreg: detail.allow_selfreg,
    selfreg_verify_email: detail.selfreg_verify_email ?? null,
    selfreg_verify_phone: detail.selfreg_verify_phone ?? null,
    use_operator_pre_check_settings: detail.use_operator_pre_check_settings,
    vehicle_route_set: detail.vehicle_route_set?.id ?? null,
  };
  const itinerary = projectItinerary(detail);
  tour[ITINERARY_DAYS] = itinerary.days;
  tour[TOUR_KML] = itinerary.kml;
  // Known context keys (plan §2 S4): text as stored; a number or boolean as its text —
  // shown and editable, never blank, never sent unless edited; a structure as stored
  // (read-only). `waiver_required` is on or absent.
  for (const k of [...CONTEXT_KEYS, 'waiver', 'waiver_link']) tour[contextPath(k)] = contextBaseline(ctx[k]);
  tour[WAIVER_REQUIRED_PATH] = waiverRequiredOf(ctx.waiver_required);
  // The tour's own pre-check-in settings: every stored key, and the known ones the
  // backend reads a missing key as (a legacy `tag_categories_enabled` /
  // `selected_tag_categories` answers for the lead booker's).
  for (const [k, v] of Object.entries(own)) tour[PRECHECK(k)] = v;
  const legacy: Record<string, unknown> = {
    lead_tag_categories_enabled: own.tag_categories_enabled,
    lead_selected_tag_categories: own.selected_tag_categories,
  };
  for (const [k, d] of Object.entries(PRECHECK_DEFAULTS)) {
    if (own[k] === undefined) tour[PRECHECK(k)] = legacy[k] ?? d;
  }

  const paths: Record<RecordKey, Record<string, unknown>> = { [rec]: tour, ...itinerary.paths };
  const raw = {} as Record<CollectionKey, Map<number, Raw>>;
  for (const c of COLLECTIONS) {
    const rows = c.rows(detail);
    raw[c.key] = new Map(rows.map((r) => [r.id, r]));
    tour[c.membership] = rows.map((r) => r.id);
    for (const r of rows) paths[recordKey(c.entity, r.id)] = c.project(r);
  }

  return {
    tourId: detail.id,
    rec,
    updatedTs: detail.updated_ts,
    paths,
    stored: {
      meeting_point: mp
        ? {
            name: mp.name,
            address: mp.address,
            comment: mp.comment,
            latitude: mp.latitude,
            longitude: mp.longitude,
            maps_url: mp.maps_url,
          }
        : null,
      review: detail.review ? { ...detail.review } : null,
      photo_sharing: detail.photo_sharing ? { ...detail.photo_sharing } : null,
      pre_check_settings: { ...own },
    },
    raw,
    itinerary: itinerary.raw,
  };
}

/** `projectTourBaseline`, recomputed only when the tour moved (`updated_ts`). */
export function useTourBaseline(detail: TourDetail): TourBaseline {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- a write always bumps updated_ts
  return useMemo(() => projectTourBaseline(detail), [detail.id, detail.updated_ts]);
}
